import * as notify from "../../../shared/notify";
import { DEFAULT_POSITION_VALUE } from "./constants";

export const setDefaultPosition = (fullPositionList, measurements = []) => {
    return fullPositionList.reduce(
        (positionCollection, position) => {
            const found = measurements.find((m) => {
                return m.fullPosition.id === position.id;
            });

            if (position.value !== DEFAULT_POSITION_VALUE && !found) {
                positionCollection.available.push(position);
            } else {
                const exists = positionCollection.active.find((a) => a.id === position.id);

                if (!exists) {
                    positionCollection.active.push(position);
                }
            }

            positionCollection.all.push(position);

            return positionCollection;
        },
        {
            available: [],
            active: [],
            all: [],
        }
    );
};

export const getPositionObject = (positionList, positionValue) => {
    if (positionList.length === 0) {
        return null;
    }

    const position = positionList.find((pos) => {
        return pos.value === positionValue;
    });

    if (!position) {
        return positionList[0];
    }

    return position;
};

const validateHWD = (usedPositions, measurements, allowZeroMeasurements, setInvalidMeasurements) => {
    const hasZeroList = [];
    usedPositions.forEach((position) => {
        const filteredMeasurements = measurements.filter((m) => m.positionValue === position.value);
        filteredMeasurements.forEach((measurement, index) => {
            const hwdList = [];
            Object.keys(measurement).forEach((key) => {
                switch (key) {
                    case "height":
                    case "width":
                    case "depth":
                        if (isNaN(measurement[key].value) || (allowZeroMeasurements ? measurement[key].value < 0 : measurement[key].value <= 0)) {
                            hwdList.push(key);

                            return;
                        }

                        break;
                    case "weight":
                        if (isNaN(measurement[key].value) || measurement[key].value < 0) {
                            hwdList.push(key);

                            return;
                        }

                        break;
                    default:
                        break;
                }
            });

            if (hwdList.length > 0) {
                hasZeroList.push({
                    key: measurement.key,
                    errors: hwdList,
                    message: `<br />${measurement.position} ${measurement.type}: ${hwdList.join(", ")}`,
                    preMessage: "Measurements <= 0: ",
                });
            }
        });
    });

    if (hasZeroList.length > 0) {
        setInvalidMeasurements(hasZeroList);

        return false;
    }

    return true;
};

const validateHasAMeasurement = (usedPositions, measurements) => {
    const measurementPositions = _.uniqBy(measurements.map((m) => m.position));

    const missingMeasurementList = [];
    usedPositions.forEach((position) => {
        if (!measurementPositions.includes(position.text)) {
            missingMeasurementList.push(position);
        }
    });

    if (missingMeasurementList.length > 0) {
        notify.error(`Missing measurements for: ${missingMeasurementList.map((m) => m.text).join(", ")}`);

        return false;
    }

    return true;
};

export const validateMeasurement = (measurement, allowZeroMeasurements) => {
    const invalidMeasurements = [];
    Object.keys(measurement).forEach((key) => {
        switch (key) {
            case "height":
            case "width":
            case "depth":
                if (isNaN(measurement[key].value) || (allowZeroMeasurements ? measurement[key].value < 0 : measurement[key].value <= 0)) {
                    invalidMeasurements.push(key);

                    return;
                }

                break;
            case "weight":
                if (isNaN(measurement[key].value) || measurement[key].value < 0) {
                    invalidMeasurements.push(key);

                    return;
                }

                break;
            default:
                break;
        }
    });

    return invalidMeasurements;
};

const buildPositionMeasurements = (measurements) => {
    return measurements.reduce((memo, m) => {
        if (!memo[m.positionValue]) {
            memo[m.positionValue] = [];
        }

        memo[m.positionValue].push(m);

        return memo;
    }, {});
};

export const hasImperialMeasurementOrIsExcluded = (measurements, isMetricClient) => {
    const positionMeasurements = buildPositionMeasurements(measurements);

    const errorList = Object.keys(positionMeasurements).reduce((errors, l) => {
        if (isMetricClient) {
            // Should ONLY have Metric measurements
            if (positionMeasurements[l].find((m) => m.type === "Imperial")) {
                errors.push(`${positionMeasurements[l][0].fullPosition.name}`);
            }
        } else {
            // Should have at least one Imperial measurement
            if (!positionMeasurements[l].find((m) => m.type === "Imperial")) {
                errors.push(`${positionMeasurements[l][0].fullPosition.name}`);
            }
        }

        return errors;
    }, []);

    return errorList;
};

export const validateHasOneMeasurement = (measurements) => {
    const positionMeasurements = buildPositionMeasurements(measurements);

    const errorList = Object.keys(positionMeasurements).reduce((errors, l) => {
        const imperialList = positionMeasurements[l].filter((m) => m.type === "Imperial") || [];
        const metricList = positionMeasurements[l].filter((m) => m.type === "Metric") || [];

        if (imperialList.length > 1) {
            errors.push(`${imperialList[0].fullPosition.name} (Imperial)`);
        }

        if (metricList.length > 1) {
            errors.push(`${imperialList[0].fullPosition.name} (Metric)`);
        }

        return errors;
    }, []);

    return errorList;
};

export const validateMeasurements = (usedPositions, measurements, isMetricClient, allowZeroMeasurements, setInvalidMeasurements) => {
    setInvalidMeasurements([]);
    const validImperialMeasurementErrors = hasImperialMeasurementOrIsExcluded(measurements, isMetricClient);

    if (validImperialMeasurementErrors.length > 0) {
        if (isMetricClient) {
            throw new Error(`This client is metric only. Imperial measurements should not be used in ${validImperialMeasurementErrors.join(", ")}`);
        } else {
            throw new Error(`An Imperial Measurement is required in ${validImperialMeasurementErrors.join(", ")}`);
        }
    }

    const hasOnlyOneMeasurementPerType = validateHasOneMeasurement(measurements);

    if (hasOnlyOneMeasurementPerType.length > 0) {
        throw new Error(`Found more than one measurement in ${hasOnlyOneMeasurementPerType.join(", ")}. Please delete extraneous measurements.`);
    }

    const hasValidMeasurement = validateHasAMeasurement(usedPositions, measurements);
    const hasHWD = validateHWD(usedPositions, measurements, allowZeroMeasurements, setInvalidMeasurements);

    return hasValidMeasurement && hasHWD ? true : false;
};

export const checkOptionsForUOM = (options, value) => {
    const newOptions = [...options];

    if (!newOptions.some((o) => o.value === value)) {
        newOptions.push({ value: value, text: value });
    }

    return newOptions;
};
